// Indian trading system: pay GST to the government (Bank › New › Pay GST).
// Input GST we hold is used first; only what is still owed leaves the bank.
// One journal entry (sourceType 'tax_adjustment') + one bank row.

import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, runTransaction } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { Button, FormField, Modal, ModalBanner } from './ui';
import { buildEntry, postEntry, entryId, linesGstPayment } from '../utils/ledger';
import { logActivity } from '../utils/activity';
import { gstBalances } from '../utils/gstSummary';
import { fmtMoney } from '../utils/currency';
import type { BankAccount, JournalEntry } from '../types';

const round2 = (n: number) => Math.round(n * 100) / 100;
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function PayGstModal({ accounts, onClose }: { accounts: BankAccount[]; onClose: () => void }) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const cur = settings.currency;
  const money = (v: number) => fmtMoney(v, cur);
  const sameCur = useMemo(() => accounts.filter(a => a.currency === cur && a.isActive !== false), [accounts, cur]);

  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [accountId, setAccountId] = useState(() => (sameCur.find(a => a.isDefault) ?? sameCur[0])?.id ?? '');
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    getDocs(collection(db, 'journalEntries'))
      .then(s => setEntries(s.docs.map(d => ({ id: d.id, ...d.data() } as JournalEntry))))
      .catch(() => { setEntries([]); setError('Could not read the books.'); });
  }, [business]);

  const bal = entries ? gstBalances(entries) : null;
  // Input GST we hold is set off against what we owe first.
  const setoff = bal ? round2(Math.max(0, Math.min(bal.input, bal.output))) : 0;
  const toPay  = bal ? round2(Math.max(0, bal.output - setoff)) : 0;
  const payNum = amount === '' ? toPay : (parseFloat(amount) || 0);

  async function save() {
    const db = getClientDb();
    if (!db || !business || !bal) return;
    if (setoff <= 0.005 && payNum <= 0.005) { setError('There is no GST to pay or set off.'); return; }
    if (payNum > 0.005 && !accountId) { setError('Choose the account it was paid from.'); return; }
    if (payNum > toPay + 0.005) { setError(`That is more than the ${money(toPay)} still owed.`); return; }
    if (!date) { setError('Pick the date.'); return; }
    setSaving(true); setError('');
    const acct = accounts.find(a => a.id === accountId);
    const txRef = doc(collection(db, 'bankTransactions'));
    try {
      await runTransaction(db, async tx => {
        const accRef = payNum > 0.005 && acct ? doc(db, 'bankAccounts', acct.id) : null;
        const accSnap = accRef ? await tx.get(accRef) : null;
        if (accRef && !accSnap?.exists()) throw new Error('That bank account no longer exists.');
        if (accRef && accSnap && acct) {
          tx.set(txRef, {
            businessId: business.id, accountId: acct.id, accountName: acct.name, currency: acct.currency,
            direction: 'out', amount: round2(payNum), date,
            description: 'GST paid to the government', referenceType: 'gst_payment',
            ...(ref.trim() ? { referenceNo: ref.trim() } : {}),
            createdAt: new Date().toISOString(), createdBy: user!.id,
          });
          tx.update(accRef, { currentBalance: round2((accSnap.data() as BankAccount).currentBalance - payNum) });
        }
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'tax_adjustment', sourceId: txRef.id,
          sourceCollection: 'bankTransactions', date, currency: cur, createdBy: user!.id,
          note: `GST paid${ref.trim() ? ` · ${ref.trim()}` : ''}`,
          lines: linesGstPayment(setoff, round2(payNum), acct?.id ?? ''),
        }), entryId('tax_adjustment', txRef.id));
        logActivity(tx, db, business.id, user, {
          module: 'bank', action: 'create', recordId: txRef.id,
          note: `GST paid · ${money(payNum)}${setoff > 0.005 ? ` · Input GST used ${money(setoff)}` : ''}${acct ? ` · ${acct.name}` : ''}`,
        });
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the GST payment.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} size="md" hideHead
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!bal}>
          {payNum > 0.005 ? `Pay ${money(payNum)}` : 'Set off Input GST'}
        </Button>
      </>}
    >
      <ModalBanner title="Pay GST" eyebrow="To the government" onClose={onClose} />
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}
      {!bal ? (
        <div className="skeleton" style={{ height: 120, borderRadius: 8 }} />
      ) : (<>
        <div className="adv-sum" style={{ marginBottom: 14 }}>
          <div><span>Output GST</span><strong>{money(bal.output)}</strong></div>
          <div><span>Input GST</span><strong>{money(bal.input)}</strong></div>
          <div><span>Set off</span><strong>{money(setoff)}</strong></div>
          <div><span>To pay</span><strong style={{ color: toPay > 0.005 ? 'var(--danger)' : 'var(--success)' }}>{money(toPay)}</strong></div>
        </div>
        {bal.net < -0.005 && (
          <p className="form-hint" style={{ marginTop: 0 }}>
            Input GST is more than Output GST: nothing to pay. {money(-bal.net)} stays as credit for later months.
          </p>
        )}
        <div className="form-row-2">
          <FormField label={`Amount paid (${cur})`}>
            <input type="number" className="no-spinner" step="0.01" min="0" value={amount}
              placeholder={toPay.toFixed(2)} onChange={e => setAmount(e.target.value)} disabled={toPay <= 0.005} />
          </FormField>
          <FormField label="Date">
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </FormField>
        </div>
        <div className="form-row-2">
          <FormField label="Paid from">
            <select value={accountId} onChange={e => setAccountId(e.target.value)} disabled={toPay <= 0.005}>
              <option value="">Select…</option>
              {sameCur.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </FormField>
          <FormField label="Challan / Ref No.">
            <input value={ref} onChange={e => setRef(e.target.value)} placeholder="Optional" />
          </FormField>
        </div>
      </>)}
    </Modal>
  );
}
